import React from "react";

import { Badge } from "@/components/ui/badge";
import { TradePositionSideBadge, type TradePositionSide } from "@/components/trade-position-side-badge";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { PineEntrySite, PineGuardContextUx } from "../../types/reportStrategyPine";
import { entryMergeStatus, guardContextMeta } from "../../utils/pineIrUi";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
  STRATEGY_PINE_DETAIL_TABLE_CLASS,
  STRATEGY_PINE_DETAIL_TABLE_SHELL_CLASS,
} from "./StrategyPineSectionCard";

export type StrategyPineEntryConditionsProps = {
  entrySites: PineEntrySite[];
  guardContextUx?: Record<string, PineGuardContextUx> | null;
  subtleText?: string;
  textColor?: string;
  borderColor?: string;
  onGoToLine?: (line: number) => void;
};

function normalizePositionSide(side: string): TradePositionSide | null {
  const normalized = side.toLowerCase();
  if (normalized === "long" || normalized === "buy") return normalized === "long" ? "long" : "buy";
  if (normalized === "short" || normalized === "sell") return normalized === "short" ? "short" : "sell";
  return null;
}
function mergeBadgeVariant(
  colorScheme: string
): "pine-inject-ready" | "pine-inject-unsupported" | "pine-inject-review" {
  if (colorScheme === "green") return "pine-inject-ready";
  if (colorScheme === "red") return "pine-inject-unsupported";
  return "pine-inject-review";
}

export function StrategyPineEntryConditions({
  entrySites,
  guardContextUx,
  onGoToLine,
}: StrategyPineEntryConditionsProps): React.ReactElement | null {
  if (!entrySites.length) return null;

  return (
    <Field data-strategy-pine-entry-conditions="" className="min-w-0 gap-2">
      <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Entry conditions</FieldLabel>
      <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
        Tessera will AND policy gates with each guard expression on compose. Expressions are not
        evaluated in the browser.
      </FieldDescription>

      <div
        data-strategy-pine-detail-table=""
        data-strategy-pine-entry-conditions-table=""
        data-shadcn-data-table=""
        className={STRATEGY_PINE_DETAIL_TABLE_SHELL_CLASS}
      >
        <Table
          className={cn(
            STRATEGY_PINE_DETAIL_TABLE_CLASS,
            "w-full min-w-full table-fixed"
          )}
        >
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[4.75rem]">Side</TableHead>
              <TableHead className="w-[5.5rem]">Entry id</TableHead>
              <TableHead>Condition</TableHead>
              <TableHead className="w-[3.25rem]">Line</TableHead>
              <TableHead className="w-[6.5rem]">Context</TableHead>
              <TableHead className="w-[5.5rem]">Merge</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entrySites.map((site, idx) => {
              const merge = entryMergeStatus(site);
              const ctx = guardContextMeta(site.guard_context, guardContextUx);
              const line = site.line_start ?? site.guard_line;
              const goLine = site.guard_line ?? site.line_start;
              const canGoToLine = Boolean(onGoToLine && goLine);
              const positionSide = site.side ? normalizePositionSide(site.side) : null;
              const guardExpr = site.guard_expr?.trim() ? site.guard_expr : "—";

              return (
                <TableRow
                  key={`${site.entry_id ?? "entry"}-${line ?? idx}-${idx}`}
                  className={cn(canGoToLine && "cursor-pointer")}
                  onClick={() => {
                    if (onGoToLine && goLine) onGoToLine(goLine);
                  }}
                >
                  <TableCell className="align-top whitespace-nowrap">
                    {site.side ? (
                      positionSide ? (
                        <TradePositionSideBadge side={positionSide} />
                      ) : (
                        site.side
                      )
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="align-top font-mono text-xs whitespace-nowrap">
                    {site.entry_id ?? "—"}
                  </TableCell>
                  <TableCell
                    data-pine-condition-cell=""
                    className="min-w-0 align-top font-mono text-xs whitespace-normal break-all"
                  >
                    {guardExpr === "—" ? (
                      "—"
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block max-w-full whitespace-normal break-all">
                            {guardExpr}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-md font-mono text-xs whitespace-pre-wrap break-all"
                        >
                          {guardExpr}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell className="align-top text-xs whitespace-nowrap">
                    {line ?? "—"}
                  </TableCell>
                  <TableCell className="align-top whitespace-nowrap">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="font-normal">
                          {ctx.label}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs">
                        {ctx.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  </TableCell>
                  <TableCell className="align-top whitespace-nowrap">
                    <Badge variant={mergeBadgeVariant(merge.colorScheme)}>{merge.label}</Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Field>
  );
}
